import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { X, Cookie, Shield, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    functional: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        console.error('Error loading cookie preferences:', e);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    setPreferences(necessaryOnly);
    savePreferences(necessaryOnly);
  };

  const handleSaveSettings = () => {
    savePreferences(preferences);
  };

  const handleToggle = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Cookie Consent Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-500">
        <Card className="max-w-4xl mx-auto p-6 shadow-2xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Cookie className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100">We value your privacy</h3>
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300 mb-4 leading-relaxed">
                We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or read our{' '}
                <a href="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline">
                  Privacy Policy
                </a>
                {' '}and{' '}
                <a href="/cookies" className="text-blue-600 dark:text-blue-400 hover:underline">
                  Cookie Policy
                </a>
                .
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleAcceptAll}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Accept All
                </Button>
                <Button
                  onClick={handleAcceptNecessary}
                  variant="outline"
                  className="border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-200"
                >
                  Necessary Only
                </Button>
                <Button
                  onClick={() => setShowSettings(true)}
                  variant="outline"
                  className="border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-800 dark:text-slate-200 flex items-center gap-2"
                >
                  <Settings className="w-4 h-4" />
                  Customize
                </Button>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAcceptNecessary}
              className="flex-shrink-0 h-8 w-8 p-0 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      </div>

      {/* Cookie Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Shield className="w-6 h-6 text-blue-600" />
              Cookie Preferences
            </DialogTitle>
            <DialogDescription>
              Manage your cookie settings. You can enable or disable different types of cookies below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Necessary Cookies */}
            <div className="border dark:border-slate-700 rounded-lg p-4 bg-slate-50 dark:bg-slate-800">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Label className="text-base font-semibold text-slate-800 dark:text-slate-100">Necessary Cookies</Label>
                  <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 text-xs rounded-full font-medium">
                    Always Active
                  </span>
                </div>
                <Switch checked={true} disabled={true} />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                These cookies are essential for the website to function properly. They enable basic features like page navigation, 
                access to secure areas, and authentication. The website cannot function properly without these cookies.
              </p>
            </div>

            {/* Functional Cookies */}
            <div className="border dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800/50">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="functional" className="text-base font-semibold text-slate-800 dark:text-slate-100 cursor-pointer">
                  Functional Cookies
                </Label>
                <Switch
                  id="functional"
                  checked={preferences.functional}
                  onCheckedChange={() => handleToggle('functional')}
                />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                These cookies enable enhanced functionality and personalization, such as remembering your preferences, 
                language settings, and providing personalized content.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="border dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800/50">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="analytics" className="text-base font-semibold text-slate-800 dark:text-slate-100 cursor-pointer">
                  Analytics Cookies
                </Label>
                <Switch
                  id="analytics"
                  checked={preferences.analytics}
                  onCheckedChange={() => handleToggle('analytics')}
                />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                These cookies help us understand how visitors interact with our website by collecting and reporting information 
                anonymously. This helps us improve our website and services.
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="border dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-800/50">
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="marketing" className="text-base font-semibold text-slate-800 dark:text-slate-100 cursor-pointer">
                  Marketing Cookies
                </Label>
                <Switch
                  id="marketing"
                  checked={preferences.marketing}
                  onCheckedChange={() => handleToggle('marketing')}
                />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                These cookies are used to track visitors across websites to display relevant advertisements. They may be set by 
                advertising partners to build a profile of your interests.
              </p>
            </div>
          </div>

          <div className="flex justify-between items-center mt-6 pt-6 border-t dark:border-slate-700">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              <a href="/privacy" className="text-blue-600 dark:text-blue-400 hover:underline mr-4">
                Privacy Policy
              </a>
              <a href="/cookies" className="text-blue-600 dark:text-blue-400 hover:underline">
                Cookie Policy
              </a>
            </div>
            <div className="flex gap-3">
              <Button
                onClick={() => setShowSettings(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveSettings}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;